if ('customElements' in window && !customElements.get('product-zoom')) {
  customElements.define(
    'product-zoom',
    class ProductZoom extends HTMLElement {
      // Around 2.5–3x is where the convention sits: enough that corners and
      // print texture become legible, not so much that the customer loses
      // track of which part of the card they are on.
      static ZOOM = 2.6;
      static EDGE_GUTTER = 72;

      connectedCallback() {
        if (this.events) return;
        this.dialog = this.querySelector('[data-product-zoom-dialog]');
        this.image = this.querySelector('[data-product-zoom-image]');
        this.sizeButton = this.querySelector('[data-product-zoom-size]');
        this.sizeLabel = this.querySelector('[data-product-zoom-size-label]');
        if (!this.dialog || !this.image || !this.sizeButton) return;

        this.events = new AbortController();
        const signal = this.events.signal;
        this.addEventListener('click', (event) => this.clickFrom(event), { signal });
        this.addEventListener('keydown', (event) => this.keyDown(event), { signal });
        this.addEventListener('pointerdown', (event) => this.inlinePointerDown(event), { signal });
        this.dialog.addEventListener('click', (event) => this.dialogClick(event), { signal });
        this.dialog.addEventListener('close', () => this.restoreFocus(), { signal });
        this.sizeButton.addEventListener('click', () => this.toggleSize(), { signal });
        this.image.addEventListener('click', () => this.toggleSize(), { signal });

        // A mouse pans the in-place inspection view by pointing. On a phone,
        // one tap enters the same view and a drag pans it like a canvas.
        // Keyboard activation retains the dialog and its explicit controls.
        this.inlineQuery = window.matchMedia('(hover: hover) and (pointer: fine) and (width >= 900px)');
        this.touchInlineQuery = window.matchMedia('(hover: none) and (pointer: coarse) and (width < 900px)');
        this.addEventListener('pointermove', (event) => this.inlineMove(event), { signal });
        this.addEventListener('pointerup', (event) => this.inlinePointerEnd(event), { signal });
        this.addEventListener('pointercancel', (event) => this.inlinePointerEnd(event, true), { signal });
        const endUnavailableMode = () => {
          if (
            (this.inlineMode === 'mouse' && !this.inlineQuery.matches) ||
            (this.inlineMode === 'touch' && !this.touchInlineQuery.matches)
          ) this.inlineEnd();
        };
        this.inlineQuery.addEventListener('change', endUnavailableMode, { signal });
        this.touchInlineQuery.addEventListener('change', endUnavailableMode, { signal });

        this.dataset.enhanced = '';
      }

      disconnectedCallback() {
        this.inlineEnd();
        this.events?.abort();
        this.events = null;
      }

      /*
       * Click or tap once and the existing image area becomes the inspection
       * window; repeat to return to the complete photo. There is deliberately
       * no lens and no second panel. A mouse position maps the source across
       * the window, while a phone drag moves that source under the finger.
       *
       * The view is painted from the 2400px source, so what the customer sees
       * is real detail rather than an upscale of the 1200px file already on
       * screen. That upscale is the failure Baymard singles out: 25% of sites
       * offer a zoom that reveals nothing.
       */
      clickFrom(event) {
        if (event.detail > 0 && this.ignoreClickUntil > Date.now()) {
          event.preventDefault();
          this.ignoreClickUntil = 0;
          return;
        }
        this.ignoreClickUntil = 0;

        if (event.target.closest('[data-product-thumb]')) {
          this.inlineEnd();
          return;
        }

        // The active viewport is wider than the image link when the media
        // card has padding. Treat that complete visible surface as the second
        // click target, including the strips beyond the link's layout box.
        if (
          this.inlineTrigger &&
          event.detail > 0 &&
          (
            (this.inlineMode === 'mouse' && this.inlineQuery.matches && event.pointerType !== 'touch') ||
            (this.inlineMode === 'touch' && this.touchInlineQuery.matches && event.pointerType !== 'mouse')
          ) &&
          this.inlineContainsPoint(event)
        ) {
          event.preventDefault();
          this.inlineEnd();
          return;
        }

        const trigger = event.target.closest('[data-product-zoom-trigger]');
        if (!trigger || !this.contains(trigger)) return;

        // A generated keyboard click has detail 0. It opens the dialog, where
        // fit/actual-size controls do not depend on a pointer position.
        if (this.inlineQuery.matches && event.detail > 0 && event.pointerType !== 'touch') {
          if (this.inlineStart(trigger, event, 'mouse')) {
            event.preventDefault();
          } else {
            this.openFrom(event);
          }
          return;
        }

        if (this.touchInlineQuery.matches && event.detail > 0 && event.pointerType !== 'mouse') {
          if (this.inlineStart(trigger, event, 'touch')) {
            event.preventDefault();
          } else {
            this.openFrom(event);
          }
          return;
        }

        this.openFrom(event);
      }

      inlineStart(trigger, event, mode = 'mouse') {
        const image = trigger.querySelector('img');
        const source = trigger.dataset.zoomSrc || trigger.href;
        const preview = image?.currentSrc || image?.src;
        if (!image || !source || source.includes('"') || preview?.includes('"')) return false;

        this.inlineEnd();
        this.inlineTrigger = trigger;
        this.inlineImage = image;
        this.inlineFrame = trigger.closest?.('[data-main-media-frame]') || trigger;
        this.inlineMode = mode;
        trigger.dataset.zoomActive = '';
        if (mode === 'touch') this.inlineFrame.dataset.zoomTouchActive = '';
        trigger.style.setProperty('--zoom-source', `url("${preview || source}")`);
        const viewport = this.inlineViewport();
        const startAt = mode === 'touch' && viewport
          ? { clientX: viewport.left + viewport.width / 2, clientY: viewport.top + viewport.height / 2 }
          : event;
        this.positionInline(startAt);

        // The preview is already decoded, so it can cover the original image
        // immediately. Swap in the 2400px source only after it is ready rather
        // than exposing the image below or flashing an empty surface.
        if (preview && preview !== source && typeof Image === 'function') {
          const request = new Image();
          this.inlineFullImage = request;
          request.onload = () => {
            if (this.inlineFullImage === request && this.inlineTrigger === trigger) {
              trigger.style.setProperty('--zoom-source', `url("${source}")`);
            }
          };
          request.src = source;
        }
        return true;
      }

      inlineMove(event) {
        if (!this.inlineTrigger) return;

        if (event.pointerType === 'mouse') {
          if (this.inlineMode !== 'mouse' || !this.inlineContainsPoint(event)) return;
          this.positionInline(event);
          return;
        }

        if (
          event.pointerType !== 'touch' ||
          this.inlineMode !== 'touch' ||
          !this.touchDrag ||
          event.pointerId !== this.touchDrag.pointerId
        ) return;

        event.preventDefault();
        const deltaX = event.clientX - this.touchDrag.startX;
        const deltaY = event.clientY - this.touchDrag.startY;
        if (Math.hypot(deltaX, deltaY) >= 6) this.touchDrag.moved = true;
        this.positionTouchInline(this.touchDrag.panX + deltaX, this.touchDrag.panY + deltaY);
      }

      inlinePointerDown(event) {
        if (
          !this.inlineTrigger ||
          this.inlineMode !== 'touch' ||
          event.pointerType !== 'touch' ||
          this.touchDrag ||
          !this.inlineContainsPoint(event)
        ) return;

        event.preventDefault();
        const style = this.inlineTrigger.style;
        this.touchDrag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          panX: Number.parseFloat(style.getPropertyValue('--zoom-x')) || 0,
          panY: Number.parseFloat(style.getPropertyValue('--zoom-y')) || 0,
          moved: false,
        };
        this.inlineFrame.dataset.zoomDragging = '';
        try {
          this.setPointerCapture?.(event.pointerId);
        } catch {}
      }

      inlinePointerEnd(event, cancelled = false) {
        if (
          event.pointerType !== 'touch' ||
          !this.touchDrag ||
          event.pointerId !== this.touchDrag.pointerId
        ) return;

        event.preventDefault();
        const moved = this.touchDrag.moved;
        this.endTouchDrag();
        if (cancelled) return;

        // A compatibility click follows pointerup on mobile. The gesture has
        // already decided whether to remain zoomed or exit, so consume it.
        this.ignoreClickUntil = Date.now() + 700;
        if (!moved) this.inlineEnd();
      }

      endTouchDrag() {
        if (!this.touchDrag) return;
        const pointerId = this.touchDrag.pointerId;
        try {
          if (this.hasPointerCapture?.(pointerId)) this.releasePointerCapture(pointerId);
        } catch {}
        if (this.inlineFrame) delete this.inlineFrame.dataset.zoomDragging;
        this.touchDrag = null;
      }

      positionTouchInline(x, y) {
        const metrics = this.inlineMetrics();
        if (!metrics) return;
        const clampBetween = (value, first, second) => (
          Math.min(Math.max(value, Math.min(first, second)), Math.max(first, second))
        );

        this.renderInline(
          metrics,
          clampBetween(x, metrics.startX, metrics.endX),
          clampBetween(y, metrics.startY, metrics.endY)
        );
      }

      inlineViewport() {
        if (!this.inlineTrigger || !this.inlineFrame) return null;
        const frameRect = this.inlineFrame.getBoundingClientRect();
        const left = frameRect.left + (this.inlineFrame.clientLeft || 0);
        const top = frameRect.top + (this.inlineFrame.clientTop || 0);
        const width = this.inlineFrame.clientWidth || frameRect.width;
        const height = this.inlineFrame.clientHeight || frameRect.height;
        if (!width || !height) return null;

        return { left, top, width, height, right: left + width, bottom: top + height };
      }

      inlineContainsPoint(event) {
        const viewport = this.inlineViewport();
        if (!viewport || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return false;

        return (
          event.clientX >= viewport.left &&
          event.clientX <= viewport.right &&
          event.clientY >= viewport.top &&
          event.clientY <= viewport.bottom
        );
      }

      inlineMetrics() {
        if (!this.inlineTrigger || !this.inlineImage) return;
        const imageRect = this.inlineImage.getBoundingClientRect();
        const triggerRect = this.inlineTrigger.getBoundingClientRect();
        const viewport = this.inlineViewport();
        if (!imageRect.width || !imageRect.height || !viewport) return;

        const zoomWidth = imageRect.width * ProductZoom.ZOOM;
        const zoomHeight = imageRect.height * ProductZoom.ZOOM;
        const gutter = Math.min(ProductZoom.EDGE_GUTTER, viewport.width / 4, viewport.height / 4);
        return {
          triggerRect,
          viewport,
          zoomWidth,
          zoomHeight,
          startX: gutter,
          startY: gutter,
          endX: viewport.width - zoomWidth - gutter,
          endY: viewport.height - zoomHeight - gutter,
        };
      }

      positionInline(event) {
        const metrics = this.inlineMetrics();
        if (!metrics) return;
        const clamp = (value, maximum) => Math.min(Math.max(value, 0), maximum);
        const pointerX = clamp(event.clientX - metrics.viewport.left, metrics.viewport.width);
        const pointerY = clamp(event.clientY - metrics.viewport.top, metrics.viewport.height);
        const x = metrics.startX + (metrics.endX - metrics.startX) * (pointerX / metrics.viewport.width);
        const y = metrics.startY + (metrics.endY - metrics.startY) * (pointerY / metrics.viewport.height);
        this.renderInline(metrics, x, y);
      }

      renderInline(metrics, x, y) {
        const style = this.inlineTrigger.style;

        style.setProperty('--zoom-left', `${metrics.viewport.left - metrics.triggerRect.left}px`);
        style.setProperty('--zoom-top', `${metrics.viewport.top - metrics.triggerRect.top}px`);
        style.setProperty('--zoom-width', `${metrics.viewport.width}px`);
        style.setProperty('--zoom-height', `${metrics.viewport.height}px`);
        style.setProperty('--zoom-image-width', `${metrics.zoomWidth}px`);
        style.setProperty('--zoom-image-height', `${metrics.zoomHeight}px`);
        style.setProperty('--zoom-x', `${x}px`);
        style.setProperty('--zoom-y', `${y}px`);
      }

      inlineEnd() {
        this.endTouchDrag();
        if (!this.inlineTrigger) return;
        if (this.inlineFullImage) {
          this.inlineFullImage.onload = null;
          this.inlineFullImage.src = '';
          this.inlineFullImage = null;
        }
        delete this.inlineTrigger.dataset.zoomActive;
        if (this.inlineFrame) delete this.inlineFrame.dataset.zoomTouchActive;
        [
          '--zoom-source',
          '--zoom-left',
          '--zoom-top',
          '--zoom-width',
          '--zoom-height',
          '--zoom-image-width',
          '--zoom-image-height',
          '--zoom-x',
          '--zoom-y',
        ].forEach((property) => this.inlineTrigger.style.removeProperty(property));
        this.inlineTrigger = null;
        this.inlineImage = null;
        this.inlineFrame = null;
        this.inlineMode = null;
      }

      keyDown(event) {
        if (event.key !== 'Escape' || !this.inlineTrigger) return;
        event.preventDefault();
        this.inlineEnd();
      }

      openFrom(event) {
        const trigger = event.target.closest('[data-product-zoom-trigger]');
        if (!trigger || !this.contains(trigger) || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.inlineEnd();
        this.lastTrigger = trigger;
        const preview = trigger.querySelector('img');
        const previewSrc = preview?.currentSrc || preview?.src;
        const zoomSrc = trigger.dataset.zoomSrc || trigger.href;

        // Open on the responsive image the customer has already downloaded,
        // then replace it with the largest available source once that request
        // finishes. This avoids a blank dialog on slower connections without
        // making every product page eagerly fetch its zoom image.
        this.image.src = previewSrc || zoomSrc;
        this.image.alt = trigger.dataset.zoomAlt || '';
        delete this.dialog.dataset.actual;
        this.renderSize();
        this.dialog.showModal();

        if (previewSrc && zoomSrc && previewSrc !== zoomSrc && typeof Image === 'function') {
          const request = new Image();
          this.fullImage = request;
          request.onload = () => {
            if (this.fullImage === request && this.dialog.open) this.image.src = zoomSrc;
          };
          request.src = zoomSrc;
        }
      }

      dialogClick(event) {
        if (event.target === this.dialog) this.dialog.close();
      }

      toggleSize() {
        if (!this.dialog.open) return;
        if ('actual' in this.dialog.dataset) delete this.dialog.dataset.actual;
        else this.dialog.dataset.actual = '';
        this.renderSize();
      }

      renderSize() {
        const actual = 'actual' in this.dialog.dataset;
        this.sizeButton.setAttribute('aria-pressed', String(actual));
        this.sizeLabel.textContent = actual
          ? this.sizeButton.dataset.fitLabel || 'Zoom out'
          : this.sizeButton.dataset.actualLabel || 'Zoom in';
      }

      restoreFocus() {
        if (this.fullImage) {
          this.fullImage.onload = null;
          this.fullImage.src = '';
          this.fullImage = null;
        }
        this.image.removeAttribute('src');
        this.lastTrigger?.focus({ preventScroll: true });
      }
    }
  );
}
